import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../HomeScreen/Navbar';
import Footer from '../HomeScreen/bottomsection/Footer';
import Loader from '../../layout/loader';
import MetaTag from '../../layout/MetaTag';
import { useLocation } from '../../context/LocationContext';
import MarketplaceProductCard from './MarketplaceProductCard';

import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import DirectionsIcon from '@mui/icons-material/Directions';
import StarRateIcon from '@mui/icons-material/StarRate';
import VerifiedIcon from '@mui/icons-material/Verified';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import LanguageIcon from '@mui/icons-material/Language';
import StorefrontIcon from '@mui/icons-material/Storefront';

function ShopPage() {
    const { slug } = useParams();
    const { location } = useLocation();
    const [shopData, setShopData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('products');
    const [selectedCategory, setSelectedCategory] = useState('All');

    useEffect(() => {
        const fetchShop = async () => {
            try {
                setLoading(true);
                const queryParts = [];
                if (location.lat != null && !isNaN(location.lat)) queryParts.push(`lat=${location.lat}`);
                if (location.lng != null && !isNaN(location.lng)) queryParts.push(`lng=${location.lng}`);
                if (location.city && location.city !== 'All Locations') queryParts.push(`city=${encodeURIComponent(location.city)}`);
                if (location.pincode) queryParts.push(`pincode=${encodeURIComponent(location.pincode)}`);
                const queryStr = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
                const res = await axios.get(`/api/marketplace/shops/${slug}${queryStr}`);
                setShopData(res.data);
            } catch (err) {
                setError(err.response?.data?.msg || 'Failed to load shop details.');
            } finally {
                setLoading(false);
            }
        };

        if (slug) {
            fetchShop();
        }
    }, [slug, location.lat, location.lng, location.city, location.pincode]);

    if (loading) return <Loader />;

    if (error || !shopData || !shopData.shop) {
        return (
            <div>
                <Navbar />
                <div style={{ textAlign: "center", padding: "100px 20px" }}>
                    <StorefrontIcon style={{ fontSize: "64px", color: "#9ca3af", marginBottom: "16px" }} />
                    <h2 style={{ fontSize: "24px", fontWeight: "900", color: "#111315" }}>Shop Not Found</h2>
                    <p style={{ color: "#6b7280", marginTop: "8px" }}>The requested store does not exist or has been relocated.</p>
                    <Link to="/" className="btn btn-primary" style={{ marginTop: "20px", padding: "10px 24px", fontWeight: "800" }}>
                        Browse Nearby Shops
                    </Link>
                </div>
                <Footer />
            </div>
        );
    }

    const { shop, products = [], categories = [] } = shopData;
    const defaultBanner = "https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=1200&q=80";

    const filteredProducts = selectedCategory === 'All'
        ? products
        : products.filter(p => p.category && p.category.toLowerCase() === selectedCategory.toLowerCase());

    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${shop.name} ${shop.address?.street || ''} ${shop.address?.city || ''}`)}`;
    const phone = shop.contact?.phone || '';
    const whatsappNumber = phone.replace(/[^0-9]/g, '');

    return (
        <div style={{ background: "#fbfaf8", minHeight: "100vh" }}>
            <MetaTag
                title={`${shop.name} | Local Store on IM Online`}
                description={`${shop.description || `Discover products, in-store deals and contact details for ${shop.name} on IM Online.`}`}
                canonical={`https://imonline.shop/shop/${shop.slug}`}
            />
            <Navbar />

            {/* ═════════════════ 1. STORE COVER BANNER & HEADER ═════════════════ */}
            <div style={{ position: "relative", height: "240px", background: "#111315", overflow: "hidden" }}>
                <img
                    src={shop.banner?.imageurl || defaultBanner}
                    alt={shop.name}
                    style={{ width: "100%", height: "100%", objectFit: "cover", opacity: 0.8 }}
                />
                <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, background: "linear-gradient(to top, rgba(17,19,21,0.85) 0%, transparent 60%)" }} />
            </div>

            <div className="container" style={{ maxWidth: "1200px", margin: "0 auto", padding: "0 20px" }}>
                <div style={{
                    background: "#ffffff",
                    borderRadius: "16px",
                    border: "1px solid #e5e7eb",
                    padding: "24px",
                    marginTop: "-80px",
                    position: "relative",
                    zIndex: 2,
                    boxShadow: "0 12px 32px rgba(17, 19, 21, 0.08)",
                    marginBottom: "24px"
                }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "20px" }}>
                        <div style={{ display: "flex", gap: "20px", alignItems: "center" }}>
                            {/* Logo */}
                            <div style={{
                                width: "90px",
                                height: "90px",
                                borderRadius: "14px",
                                background: "#ffffff",
                                border: "3px solid #ffffff",
                                boxShadow: "0 4px 14px rgba(0,0,0,0.12)",
                                display: "grid",
                                placeItems: "center",
                                overflow: "hidden",
                                flexShrink: 0
                            }}>
                                {shop.logo?.imageurl ? (
                                    <img src={shop.logo.imageurl} alt={shop.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                ) : (
                                    <span style={{ fontSize: "36px", fontWeight: "900", color: "#a85050" }}>{shop.name.charAt(0)}</span>
                                )}
                            </div>

                            {/* Store Info */}
                            <div>
                                <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                                    <h1 style={{ fontSize: "24px", fontWeight: "900", color: "#111315", margin: 0 }}>
                                        {shop.name}
                                    </h1>
                                    {(shop.isVerified || shop.isTrusted) && (
                                        <VerifiedIcon style={{ color: "#2563eb", fontSize: "20px" }} titleAccess="Trusted Verified Seller" />
                                    )}
                                    {shop.isOpen ? (
                                        <span style={{ background: "#dcfce7", color: "#166534", fontSize: "11px", fontWeight: "800", padding: "3px 9px", borderRadius: "99px", display: "inline-flex", alignItems: "center", gap: "5px" }}>
                                            <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#16a34a" }}></span>
                                            Open Now
                                        </span>
                                    ) : (
                                        <span style={{ background: "#fee2e2", color: "#991b1b", fontSize: "11px", fontWeight: "800", padding: "3px 9px", borderRadius: "99px", display: "inline-flex", alignItems: "center", gap: "5px" }}>
                                            <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#dc2626" }}></span>
                                            {shop.statusText || "Closed Now"}
                                        </span>
                                    )}
                                    {shop.detailText && (
                                        <span style={{ fontSize: "12px", color: shop.isOpen ? "#15803d" : "#b91c1c", fontWeight: "700" }}>
                                            {shop.detailText}
                                        </span>
                                    )}
                                </div>

                                <div style={{ fontSize: "13.5px", color: "#4b5563", marginTop: "4px" }}>
                                    {shop.category || "Fashion & Lifestyle"} &middot; {shop.businessType || "Physical Store"}
                                </div>

                                <div style={{ display: "flex", alignItems: "center", gap: "16px", marginTop: "8px", flexWrap: "wrap", fontSize: "13px", color: "#6b7280" }}>
                                    <span style={{ display: "flex", alignItems: "center", gap: "4px", color: "#111315", fontWeight: "700" }}>
                                        <LocationOnIcon style={{ color: "#a85050", fontSize: "16px" }} />
                                        {shop.distanceFormatted} ({shop.address?.city || "Delhi"})
                                    </span>
                                    <span style={{ display: "flex", alignItems: "center", gap: "4px", color: "#d97706", fontWeight: "800" }}>
                                        <StarRateIcon style={{ fontSize: "16px" }} />
                                        {shop.ratings} ({shop.numReviews || 18} reviews)
                                    </span>
                                    <span style={{ color: "#4b5563", fontWeight: "700" }}>
                                        📦 {products.length} Products Live
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Quick Contact CTAs */}
                        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                            {phone && (
                                <a
                                    href={`tel:${phone}`}
                                    style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "6px",
                                        padding: "10px 18px",
                                        borderRadius: "8px",
                                        background: "#111315",
                                        color: "#ffffff",
                                        textDecoration: "none",
                                        fontSize: "13px",
                                        fontWeight: "800"
                                    }}
                                >
                                    <PhoneIcon style={{ fontSize: "16px" }} />
                                    Call Shop
                                </a>
                            )}
                            {phone && (
                                <a
                                    href={`https://wa.me/91${whatsappNumber}?text=${encodeURIComponent(`Hi ${shop.name}, I found your store on IM Online!`)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "6px",
                                        padding: "10px 18px",
                                        borderRadius: "8px",
                                        background: "#25d366",
                                        color: "#ffffff",
                                        textDecoration: "none",
                                        fontSize: "13px",
                                        fontWeight: "800"
                                    }}
                                >
                                    <WhatsAppIcon style={{ fontSize: "16px" }} />
                                    WhatsApp
                                </a>
                            )}
                            <a
                                href={mapsUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    padding: "10px 18px",
                                    borderRadius: "8px",
                                    background: "#f3f4f6",
                                    color: "#111315",
                                    textDecoration: "none",
                                    fontSize: "13px",
                                    fontWeight: "800",
                                    border: "1px solid #d1d5db"
                                }}
                            >
                                <DirectionsIcon style={{ fontSize: "16px" }} />
                                Get Directions
                            </a>
                        </div>
                    </div>

                    {/* Fulfillment Capabilities Checklist Banner */}
                    <div style={{
                        marginTop: "18px",
                        padding: "12px 16px",
                        borderRadius: "10px",
                        background: "#fdf6f5",
                        border: "1.5px solid #fed7d7",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: "10px"
                    }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: "#111315", fontWeight: "700" }}>
                            <span style={{ fontSize: "16px" }}>🏪</span>
                            <span>Ways to Buy at {shop.name}:</span>
                        </div>
                        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", fontSize: "12px", color: "#166534", fontWeight: "800" }}>
                            <span style={{ background: "#dcfce7", padding: "3px 8px", borderRadius: "6px" }}>✓ Home Delivery</span>
                            <span style={{ background: "#dcfce7", padding: "3px 8px", borderRadius: "6px" }}>✓ Store Pickup</span>
                            <span style={{ background: "#dcfce7", padding: "3px 8px", borderRadius: "6px" }}>✓ Product Reservation</span>
                            <span style={{ background: "#dcfce7", padding: "3px 8px", borderRadius: "6px" }}>✓ Direct Call / WhatsApp</span>
                        </div>
                    </div>
                </div>

                {/* ═════════════════ 2. TABS NAVIGATION ═════════════════ */}
                <div style={{
                    display: "flex",
                    gap: "8px",
                    borderBottom: "1px solid #e5e7eb",
                    marginBottom: "24px",
                    overflowX: "auto"
                }}>
                    {[
                        { id: 'products', label: `Shop Products (${products.length})` },
                        { id: 'about', label: 'About & Brand Story' },
                        { id: 'gallery', label: `Store Gallery (${shop.gallery?.length || 0})` },
                        { id: 'info', label: 'Store Info & Hours' }
                    ].map(tab => (
                        <button
                            key={tab.id}
                            type="button"
                            onClick={() => setActiveTab(tab.id)}
                            style={{
                                background: "none",
                                border: "none",
                                borderBottom: activeTab === tab.id ? "3px solid #a85050" : "3px solid transparent",
                                color: activeTab === tab.id ? "#a85050" : "#6b7280",
                                padding: "12px 18px",
                                fontSize: "14px",
                                fontWeight: "800",
                                cursor: "pointer",
                                whiteSpace: "nowrap"
                            }}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* ═════════════════ 3. TAB CONTENTS ═════════════════ */}

                {/* ── PRODUCTS TAB ── */}
                {activeTab === 'products' && (
                    <div>
                        {/* Category Filter Pills */}
                        {categories.length > 0 && (
                            <div style={{ display: "flex", gap: "8px", marginBottom: "20px", overflowX: "auto", paddingBottom: "4px" }}>
                                <button
                                    type="button"
                                    onClick={() => setSelectedCategory('All')}
                                    style={{
                                        background: selectedCategory === 'All' ? "#111315" : "#ffffff",
                                        color: selectedCategory === 'All' ? "#ffffff" : "#4b5563",
                                        border: "1px solid #d1d5db",
                                        borderRadius: "99px",
                                        padding: "6px 16px",
                                        fontSize: "12.5px",
                                        fontWeight: "800",
                                        cursor: "pointer"
                                    }}
                                >
                                    All ({products.length})
                                </button>
                                {categories.map(cat => (
                                    <button
                                        key={cat.name}
                                        type="button"
                                        onClick={() => setSelectedCategory(cat.name)}
                                        style={{
                                            background: selectedCategory === cat.name ? "#111315" : "#ffffff",
                                            color: selectedCategory === cat.name ? "#ffffff" : "#4b5563",
                                            border: "1px solid #d1d5db",
                                            borderRadius: "99px",
                                            padding: "6px 16px",
                                            fontSize: "12.5px",
                                            fontWeight: "800",
                                            cursor: "pointer",
                                            whiteSpace: "nowrap"
                                        }}
                                    >
                                        {cat.name} ({cat.count})
                                    </button>
                                ))}
                            </div>
                        )}

                        {filteredProducts.length > 0 ? (
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: "20px", marginBottom: "60px" }}>
                                {filteredProducts.map(prod => (
                                    <MarketplaceProductCard key={prod._id} product={prod} />
                                ))}
                            </div>
                        ) : (
                            <div style={{ textAlign: "center", padding: "60px 20px", background: "#ffffff", borderRadius: "12px", border: "1px solid #e5e7eb", marginBottom: "60px" }}>
                                <p style={{ color: "#6b7280", margin: 0 }}>No products found in this category.</p>
                            </div>
                        )}
                    </div>
                )}

                {/* ── ABOUT & STORY TAB ── */}
                {activeTab === 'about' && (
                    <div style={{ background: "#ffffff", padding: "28px", borderRadius: "14px", border: "1px solid #e5e7eb", marginBottom: "60px" }}>
                        <h3 style={{ fontSize: "18px", fontWeight: "900", color: "#111315", marginBottom: "14px" }}>
                            About {shop.name}
                        </h3>
                        <p style={{ fontSize: "14px", color: "#4b5563", lineHeight: "1.7", marginBottom: "24px" }}>
                            {shop.description || `${shop.name} is a verified local business providing high quality footwear, apparel, and lifestyle accessories on the IM Online marketplace.`}
                        </p>

                        {shop.brandStory && (
                            <div style={{ background: "#fbfaf8", padding: "20px", borderRadius: "12px", border: "1px solid #edf0f4", marginBottom: "24px" }}>
                                <h4 style={{ fontSize: "16px", fontWeight: "900", color: "#111315", marginBottom: "8px" }}>
                                    Our Brand Story &amp; Heritage
                                </h4>
                                <p style={{ fontSize: "13.5px", color: "#4b5563", lineHeight: "1.6", margin: 0 }}>
                                    {shop.brandStory}
                                </p>
                            </div>
                        )}

                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px" }}>
                            <div style={{ background: "#f9fafb", padding: "14px", borderRadius: "10px" }}>
                                <span style={{ fontSize: "11px", fontWeight: "800", color: "#6b7280", textTransform: "uppercase" }}>Years in Business</span>
                                <div style={{ fontSize: "16px", fontWeight: "900", color: "#111315", marginTop: "2px" }}>
                                    {shop.yearsInBusiness ? `${shop.yearsInBusiness} Years` : "Established Local Shop"}
                                </div>
                            </div>
                            <div style={{ background: "#f9fafb", padding: "14px", borderRadius: "10px" }}>
                                <span style={{ fontSize: "11px", fontWeight: "800", color: "#6b7280", textTransform: "uppercase" }}>Specialization</span>
                                <div style={{ fontSize: "16px", fontWeight: "900", color: "#111315", marginTop: "2px" }}>
                                    {shop.specialization || shop.category || "Premium Quality"}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ── GALLERY TAB ── */}
                {activeTab === 'gallery' && (
                    <div style={{ background: "#ffffff", padding: "28px", borderRadius: "14px", border: "1px solid #e5e7eb", marginBottom: "60px" }}>
                        <h3 style={{ fontSize: "18px", fontWeight: "900", color: "#111315", marginBottom: "8px" }}>
                            Store Photos &amp; Showroom
                        </h3>
                        <p style={{ color: "#6b7280", fontSize: "13px", marginBottom: "20px" }}>
                            Take a virtual look inside our store, workshop, and packaging setup.
                        </p>

                        {shop.gallery && shop.gallery.length > 0 ? (
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: "16px" }}>
                                {shop.gallery.map((img, idx) => (
                                    <div key={idx} style={{ borderRadius: "10px", overflow: "hidden", border: "1px solid #e5e7eb", background: "#f9fafb" }}>
                                        <img src={img.imageurl} alt={img.title || "Store photo"} style={{ width: "100%", height: "180px", objectFit: "cover" }} />
                                        {img.title && (
                                            <div style={{ padding: "10px 12px", fontSize: "12.5px", fontWeight: "700", color: "#111315" }}>
                                                {img.title}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div style={{ textAlign: "center", padding: "40px", color: "#6b7280" }}>
                                No store photos uploaded yet.
                            </div>
                        )}
                    </div>
                )}

                {/* ── INFO & HOURS TAB ── */}
                {activeTab === 'info' && (
                    <div style={{ background: "#ffffff", padding: "28px", borderRadius: "14px", border: "1px solid #e5e7eb", marginBottom: "60px" }}>
                        <div className="row g-4">
                            <div className="col-md-6">
                                <h3 style={{ fontSize: "17px", fontWeight: "900", color: "#111315", marginBottom: "14px" }}>
                                    Physical Address
                                </h3>
                                <div style={{ display: "flex", alignItems: "flex-start", gap: "10px", color: "#4b5563", fontSize: "14px", lineHeight: "1.6" }}>
                                    <LocationOnIcon style={{ color: "#a85050", fontSize: "22px", flexShrink: 0 }} />
                                    <div>
                                        <strong>{shop.name}</strong><br />
                                        {shop.address?.street || "Main Market"}<br />
                                        {shop.address?.city || "Delhi"}, {shop.address?.state || "Delhi"} - {shop.address?.pincode || "110001"}<br />
                                        <a href={mapsUrl} target="_blank" rel="noopener noreferrer" style={{ color: "#2563eb", fontWeight: "700", textDecoration: "underline", display: "inline-block", marginTop: "6px" }}>
                                            Open in Google Maps &rarr;
                                        </a>
                                    </div>
                                </div>
                            </div>

                            <div className="col-md-6">
                                <h3 style={{ fontSize: "17px", fontWeight: "900", color: "#111315", marginBottom: "14px" }}>
                                    Operating Hours &amp; Contact
                                </h3>
                                <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#4b5563", fontSize: "13.5px", marginBottom: "12px" }}>
                                    <AccessTimeIcon style={{ color: "#16a34a", fontSize: "18px" }} />
                                    <span>{shop.businessHours || "Mon - Sat: 10:00 AM - 8:00 PM"}</span>
                                </div>

                                {shop.socialLinks && (
                                    <div style={{ marginTop: "20px" }}>
                                        <span style={{ fontSize: "12px", fontWeight: "800", color: "#6b7280", textTransform: "uppercase", display: "block", marginBottom: "8px" }}>
                                            Social Channels
                                        </span>
                                        <div style={{ display: "flex", gap: "12px" }}>
                                            {shop.socialLinks.instagram && (
                                                <a href={shop.socialLinks.instagram} target="_blank" rel="noopener noreferrer" style={{ color: "#e1306c" }}>
                                                    <InstagramIcon />
                                                </a>
                                            )}
                                            {shop.socialLinks.facebook && (
                                                <a href={shop.socialLinks.facebook} target="_blank" rel="noopener noreferrer" style={{ color: "#1877f2" }}>
                                                    <FacebookIcon />
                                                </a>
                                            )}
                                            {shop.socialLinks.website && (
                                                <a href={shop.socialLinks.website} target="_blank" rel="noopener noreferrer" style={{ color: "#4b5563" }}>
                                                    <LanguageIcon />
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <Footer />
        </div>
    );
}

export default ShopPage;
